import { useEffect, useState } from 'react';
import {
  applyDisplayScaleCss,
  computeDisplayScale,
  dispatchDisplayScaleChange,
  type DisplayScaleSnapshot,
} from '../mapDisplayScale';

function readSnapshot(): DisplayScaleSnapshot {
  return computeDisplayScale(
    window.innerWidth,
    window.innerHeight,
    window.devicePixelRatio || 1,
  );
}

/**
 * Watches viewport / DPR changes and publishes ``--digisaka-ui-scale`` for large-monitor
 * and projector readability.
 */
export function useDisplayScale(): DisplayScaleSnapshot {
  const [snapshot, setSnapshot] = useState<DisplayScaleSnapshot>(() =>
    typeof window !== 'undefined'
      ? readSnapshot()
      : { uiScale: 1, mapZoomOffset: 0, isPresentationDisplay: false },
  );

  useEffect(() => {
    let frame = 0;

    const publish = () => {
      frame = 0;
      const next = readSnapshot();
      setSnapshot((prev) => {
        const changed =
          Math.abs(prev.uiScale - next.uiScale) > 0.01 ||
          prev.isPresentationDisplay !== next.isPresentationDisplay;
        if (changed) {
          applyDisplayScaleCss(next);
          dispatchDisplayScaleChange(next);
        }
        return next;
      });
    };

    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(publish);
    };

    applyDisplayScaleCss(readSnapshot());
    window.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('resize', schedule);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('resize', schedule);
    };
  }, []);

  return snapshot;
}
